import type { Context, FC, ReactNode } from 'react';
import React, { createContext, useMemo } from 'react';

/**
 * Pieces the view page header renders instead of its own when the page is viewed through an access
 * link. All optional; with none supplied the header is unchanged.
 */
export type AccessLinkHeaderReplacementsType = {
	/**
	 * Share control replacing the header's own, whose data query never resolves for a token-only
	 * viewer and so leaves it permanently disabled.
	 */
	shareButton?: ReactNode;
	/**
	 * Page title rendered where the breadcrumbs would be, which are hidden because their trail names
	 * pages the viewer cannot access. The header owns truncation and fade-in.
	 */
	title?: ReactNode;
	/**
	 * Notice rendered inside the header's fixed container, which is what lets it overlap the scroll
	 * container's scrollbar the way the header already does.
	 */
	banner?: ReactNode;
	/**
	 * Height `banner` occupies, so consumers can reserve the same space. Supplied rather than
	 * measured so the server and the client agree and nothing shifts after hydration.
	 */
	bannerHeight?: number;
};

export const AccessLinkHeaderReplacementsContext: Context<AccessLinkHeaderReplacementsType> =
	createContext<AccessLinkHeaderReplacementsType>({});

type AccessLinkHeaderReplacementsProviderProps = AccessLinkHeaderReplacementsType & {
	children?: ReactNode;
};

export const AccessLinkHeaderReplacementsProvider: FC<
	AccessLinkHeaderReplacementsProviderProps
> = ({ children, shareButton, title, banner, bannerHeight }) => {
	const value = useMemo(
		() => ({ shareButton, title, banner, bannerHeight }),
		[shareButton, title, banner, bannerHeight],
	);

	return (
		<AccessLinkHeaderReplacementsContext.Provider value={value}>
			{children}
		</AccessLinkHeaderReplacementsContext.Provider>
	);
};
